import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Icon } from '@iconify/react';
import kkpService from '../../services/kkpService';
import { BRIEF_ACCEPTED_FILE_TYPES, formatBriefFileSize } from '../../utils/kkpBriefFichierUtils';
import { AppButton } from '../ui';
import useAppConfirm from '../../hooks/useAppConfirm';
import '../../styles/kkp-brief-fichiers.scss';

function fileIcon(mime = '') {
  if (mime.startsWith('image/')) return 'mdi:file-image-outline';
  if (mime.startsWith('video/')) return 'mdi:file-video-outline';
  if (mime === 'application/pdf') return 'mdi:file-pdf-box';
  if (mime.startsWith('text/')) return 'mdi:file-document-outline';
  return 'mdi:paperclip';
}

const KkpCreaPubAttachmentsEditor = ({
  venteId,
  piecesJointes = [],
  onChange,
  onError,
  disabled = false,
}) => {
  const inputRef = useRef(null);
  const [items, setItems] = useState(piecesJointes);
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [previewUrls, setPreviewUrls] = useState({});
  const { ask, modal } = useAppConfirm();

  useEffect(() => {
    setItems(piecesJointes);
  }, [piecesJointes, venteId]);

  useEffect(() => {
    setPreviewUrls({});
  }, [venteId, items]);

  useEffect(() => () => {
    Object.values(previewUrls).forEach((url) => URL.revokeObjectURL(url));
  }, [previewUrls]);

  useEffect(() => {
    let cancelled = false;

    items.forEach(async (item) => {
      if (!venteId || !item.is_image || previewUrls[item.id]) return;
      try {
        const blob = await kkpService.fetchCreaPubFichierBlob(venteId, item.id, { inline: true });
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        setPreviewUrls((prev) => (prev[item.id] ? prev : { ...prev, [item.id]: url }));
      } catch (err) {
        if (!cancelled) onError?.(err.message);
      }
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, venteId]);

  const syncItems = useCallback((nextItems) => {
    setItems(nextItems);
    onChange?.(nextItems);
  }, [onChange]);

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !venteId) return;

    setUploading(true);
    try {
      const uploaded = await kkpService.uploadCreaPubFichier(venteId, file);
      syncItems([...items, uploaded]);
    } catch (err) {
      onError?.(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (fileId) => {
    if (!venteId) return;
    const ok = await ask({
      title: 'Supprimer cette création ?',
      message: 'Le fichier sera retiré des créations de cette publicité.',
      confirmLabel: 'Supprimer',
      confirmVariant: 'danger',
    });
    if (!ok) return;

    setDeletingId(fileId);
    try {
      await kkpService.deleteCreaPubFichier(venteId, fileId);
      if (previewUrls[fileId]) {
        URL.revokeObjectURL(previewUrls[fileId]);
        setPreviewUrls((prev) => {
          const next = { ...prev };
          delete next[fileId];
          return next;
        });
      }
      syncItems(items.filter((item) => item.id !== fileId));
    } catch (err) {
      onError?.(err.message);
    } finally {
      setDeletingId(null);
    }
  };

  const handleDownload = async (item) => {
    if (!venteId) return;
    setDownloadingId(item.id);
    try {
      await kkpService.downloadCreaPubFichier(venteId, item.id, item.nom);
    } catch (err) {
      onError?.(err.message);
    } finally {
      setDownloadingId(null);
    }
  };

  if (!venteId) {
    return (
      <div className="kkp-brief-fichiers kkp-brief-fichiers--disabled mt-3">
        <p className="text-secondary-light text-sm mb-0">
          Impossible d’ajouter une création sans publicité.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="kkp-brief-fichiers mt-3">
        <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
          <label className="form-label fw-medium mb-0">Créations déposées</label>
          <AppButton
            type="button"
            size="sm"
            variant="outline-primary"
            onClick={() => inputRef.current?.click()}
            disabled={disabled}
            loading={uploading}
            loadingLabel="Envoi…"
            icon="mdi:paperclip"
          >
            Ajouter un fichier
          </AppButton>
          <input
            ref={inputRef}
            type="file"
            className="d-none"
            accept={BRIEF_ACCEPTED_FILE_TYPES}
            onChange={handleUpload}
            disabled={disabled || uploading}
          />
        </div>

        <p className="text-secondary-light text-sm mb-2">
          Images, PDF, documents… (max 1 Go par fichier). Vous pouvez ajouter ou remplacer a posteriori.
        </p>

        {items.length === 0 ? (
          <div className="kkp-brief-fichiers__empty text-secondary-light text-sm">
            Aucune création déposée pour le moment.
          </div>
        ) : (
          <ul className="kkp-brief-fichiers__list list-unstyled mb-0">
            {items.map((item) => (
              <li key={item.id} className="kkp-brief-fichiers__item">
                {item.is_image && previewUrls[item.id] ? (
                  <img
                    src={previewUrls[item.id]}
                    alt={item.nom}
                    className="kkp-brief-fichiers__icon"
                    style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }}
                  />
                ) : (
                  <Icon icon={fileIcon(item.mime)} className="kkp-brief-fichiers__icon" />
                )}
                <div className="kkp-brief-fichiers__meta">
                  <span className="kkp-brief-fichiers__name">{item.nom}</span>
                  <span className="kkp-brief-fichiers__size">{formatBriefFileSize(item.taille)}</span>
                </div>
                <div className="d-flex gap-1">
                  <AppButton
                    type="button"
                    size="sm"
                    variant="outline-secondary"
                    title="Télécharger"
                    loading={downloadingId === item.id}
                    icon="mdi:download"
                    onClick={() => handleDownload(item)}
                  />
                  <AppButton
                    type="button"
                    size="sm"
                    variant="outline-danger"
                    title="Supprimer"
                    loading={deletingId === item.id}
                    icon="mdi:delete-outline"
                    onClick={() => handleDelete(item.id)}
                  />
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
      {modal}
    </>
  );
};

export default KkpCreaPubAttachmentsEditor;
